import { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';

import { getPlans } from 'imports/checkout/api/helpers';
import { getCountry, helperCurrencyFormatter } from 'imports/checkout/api/utils';
import useBilling from '/imports/checkout/api/useBilling';
import { useResponsive } from '/imports/core/api/responsiveContext';
import useIntl from '/imports/core/api/useIntl';
import useDomain from '/imports/core/hooks/useDomain';
import { replaceString } from '/imports/core/ui/helpers';
import { BulletText, ContentWrapper, LinkText, StyledText, Text } from '/imports/refundPolicy/ui/atoms';
import Bullet from '/imports/refundPolicy/ui/components/Bullet';
import Header from '/imports/refundPolicy/ui/components/Header';
import { getContactNumber, getCountryWiseCurrency, getRoute, isBlogDomain } from '/lib/helpers';
import { COUNTRY_LIST } from 'imports/job-tracking/api/constants';

const SubscriptionCancellation = ({ getContactSupportLink }) => {
  const { t, locale } = useIntl();
  const { getDomainName } = useDomain();
  const { host } = useResponsive();
  const { hideOneYear, currency } = useBilling();
  const split = t('refund.policy_info6').split('*')[0];
  const slug = hideOneYear ? split : t('refund.policy_info6');
  const countryCode = getCountry();
  const country = COUNTRY_LIST.find((country) => country?.countryCode === countryCode);
  const [pricingPlans, setPricingPlans] = useState([]);

  useEffect(() => {
    const fetchPlans = async () => {
      const currencyUpdate = getCountryWiseCurrency(countryCode) || currency || 'EUR';
      const fetchedData = await getPlans(currencyUpdate);

      const firstPlan = fetchedData[0];
      const priceParts = helperCurrencyFormatter(firstPlan.trialAmount, currencyUpdate, locale);
      const currencySymbol = priceParts.find((part) => part.type === 'currency')?.value || '';
      const currencyPrefix = priceParts.find((part) => part.type === 'currency-prefix')?.value || '';
      const currencyPostfix = priceParts.find((part) => part.type === 'currency-postfix')?.value || '';

      const trialAmounts = fetchedData.reduce((acc, plan) => {
        const formattedPrice = `${currencyPrefix}${currencySymbol}${plan.trialAmount}${currencyPostfix}`;
        const formattedAmount = `${currencyPrefix}${currencySymbol}${plan.amount}${currencyPostfix}`;

        if (plan.name === '14-DAY LIMITED ACCESS') {
          acc.TRAIL_TWO = formattedPrice;
          acc.PER_MONTH = formattedAmount;
        } else if (plan.name === 'MONTHLY ACCESS') {
          acc.TRIAL_MONTH = formattedPrice;
          acc.PER_YEAR = formattedAmount;
        } else if (plan.name === '14-DAY FULL ACCESS') {
          acc.TRIAL_ONE = formattedPrice;
        }
        return acc;
      }, {});

      const plans = [
        {
          title: `${country?.name} - ${currencyUpdate}(${currencyPrefix}${currencySymbol}${currencyPostfix})`,
          pricing: replaceString(slug, {
            TRAIL_ONE: trialAmounts.TRIAL_ONE || '$1.95',
            TRAIL_TWO: trialAmounts.TRAIL_TWO || '$1.45',
            PER_MONTH: trialAmounts.PER_MONTH || '$29.99',
            TRIAL_MONTH: trialAmounts.TRIAL_MONTH || '$10.45',
            PER_YEAR: trialAmounts.PER_YEAR || '$125.40',
          }),
        },
      ];

      setPricingPlans(plans);
    };

    fetchPlans();
  }, []);

  return (
    <Fragment>
      <Header>{t('refund.policy_title')}</Header>
      <ContentWrapper>
        <Text>{t('refund.policy_info')}</Text>
        <Bullet>
          <LinkText
            dangerouslySetInnerHTML={{
              __html: replaceString(t('refund.policy_1'), {
                '/contact-us': getRoute('/contact-us', locale, false, host, isBlogDomain(host)),
              }),
            }}
          />
        </Bullet>
        <Bullet>
          <LinkText
            dangerouslySetInnerHTML={{
              __html: getContactSupportLink(),
            }}
          />
        </Bullet>
        <Bullet>
          <LinkText dangerouslySetInnerHTML={{ __html: getContactNumber(host, t('refund.para2_sub3'), t) }} />
        </Bullet>
        <Bullet disableBullet>{t('refund.policy_4')}</Bullet>
        <Bullet disableBullet isList>
          1){' '}
          <LinkText
            styled
            dangerouslySetInnerHTML={{
              __html: replaceString(t('refund.policy_4_step1'), {
                'resumedone.co': getDomainName(),
                '/login': getRoute('/login', locale, false, host, isBlogDomain(host)),
              }),
            }}
          />
        </Bullet>
        <Bullet disableBullet isList>
          2){' '}
          <LinkText
            styled
            dangerouslySetInnerHTML={{
              __html: replaceString(t('refund.policy_4_step2'), {
                'resumedone.co': getDomainName(),
                '/account': getRoute('/account', locale, false, host, isBlogDomain(host)),
              }),
            }}
          />
        </Bullet>
        <Bullet disableBullet isList>
          3) {t('refund.policy_4_step3')}
        </Bullet>
        <Text>
          <span dangerouslySetInnerHTML={{ __html: t('refund.policy_info2') }} />
        </Text>
        <SubHeading>{t('refund.billing_types')}</SubHeading>
        {pricingPlans &&
          pricingPlans.map((plan, index) => (
            <Fragment key={index}>
              <Bullet>{plan.title}</Bullet>
              <Itext>{plan.pricing}</Itext>
            </Fragment>
          ))}
        <StyledText footerText>{t('refund.warning')}</StyledText>
      </ContentWrapper>
    </Fragment>
  );
};

export default SubscriptionCancellation;

const Itext = styled(BulletText)`
  font-size: 12px;
  padding-top: 0;
  padding-bottom: 0;
`;

const SubHeading = styled(Text)`
  padding-top: 10px;
  font-weight: 600;
`;
